// The telecaller's working screen. Each student is a card with the two things a caller does first:
//   Call      -> a tel: link, which opens the phone's dialler (on a computer the OS offers a calling app)
//   WhatsApp  -> a wa.me link, which opens WhatsApp (the app on a phone, WhatsApp Web on a computer)
// then "Log call", which records the outcome. A final outcome locks the student. Locked students are listed (so the
// caller can see what they closed) but offer no actions.
import { useState } from 'react';

import { ConfirmDialog, Drawer, Notice, StatusChip } from 'src/front-components/lead-desk/kit';
import { Icon } from 'src/front-components/lead-desk/icons';
import { Field } from 'src/front-components/lead-desk/student-form';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { DetailsDrawer } from 'src/front-components/lead-desk/student-drawers';
import { ShellCard, ShellChip, ShellEmptyState, ShellGrid, ShellStat } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, DeskSelect, ErrorLine } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import { logCall } from 'src/front-components/utils/lead-desk-data';
import {
  callSections,
  DESK_STATUS_COLORS,
  DESK_STATUS_LABELS,
  dueLabel,
  EMPTY_OUTCOME,
  FOLLOW_UP_LABELS,
  isFinalStatus,
  isLockedFor,
  LOCKED_MESSAGE,
  MAX_NOTE,
  statusChoices,
  studentPhone,
  telUrl,
  validateOutcome,
  whatsappGreeting,
  whatsappUrl,
  type CallOutcomeForm,
  type OutcomeErrors,
  type StudentRow,
} from 'src/utils/lead-desk';

// Darker than the apps' own logo greens on purpose: white text on #25d366 is only 1.98:1 and on #16a34a 3.3:1. These are the two buttons a telecaller
// presses all day, often outdoors on a phone, so they meet the 4.5:1 text standard (5.0:1 and 5.4:1) and still read as call-green and WhatsApp-green.
const CALL_GREEN = '#15803d';
const WHATSAPP_GREEN = '#0f7a3f';

const linkStyle = (colour: string, disabled: boolean, compact = false): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: compact ? 4 : SHELL_SPACE[1.5],
  flex: compact ? undefined : 1,
  minHeight: compact ? 30 : 40,
  padding: compact ? '4px 9px' : `0 ${SHELL_SPACE[3]}`,
  borderRadius: compact ? SHELL_RADIUS.sm : SHELL_RADIUS.md,
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: compact ? '12px' : SHELL_TYPE.body.fontSize,
  lineHeight: 1,
  background: disabled ? v('background-tertiary') : colour,
  color: disabled ? v('font-color-tertiary') : '#fff',
  pointerEvents: disabled ? 'none' : 'auto',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  flexShrink: 0,
});

/** The two contact links. A number that is not a valid 10-digit mobile gets a disabled, labelled button instead of a dead link. */
export const ContactButtons = ({
  student,
  callerName,
  compact = false,
  onCallInitiated,
}: {
  student: StudentRow;
  callerName: string;
  compact?: boolean;
  onCallInitiated?: () => void;
}) => {
  const phone = studentPhone(student);
  const tel = telUrl(phone);
  const wa = whatsappUrl(phone, whatsappGreeting(student.name, callerName));

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCallInitiated) onCallInitiated();
  };

  return (
    <div style={{ display: 'flex', gap: compact ? 6 : SHELL_SPACE[2], flexWrap: compact ? 'nowrap' : 'wrap', alignItems: 'center', width: '100%' }}>
      <a
        href={tel ?? undefined}
        onClick={handleCall}
        aria-label={`Call ${student.name ?? 'student'} on ${phone}`}
        aria-disabled={tel === null}
        style={linkStyle(CALL_GREEN, tel === null, compact)}
      >
        <Icon name="phone" size={compact ? 13 : 16} />
        {tel === null ? 'No number' : 'Call'}
      </a>
      <a
        href={wa ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label={`WhatsApp ${student.name ?? 'student'} on ${phone}`}
        aria-disabled={wa === null}
        style={linkStyle(WHATSAPP_GREEN, wa === null, compact)}
      >
        <svg width={compact ? 13 : 16} height={compact ? 13 : 16} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12.04 2a9.9 9.9 0 0 0-8.5 14.9L2 22l5.25-1.38A9.9 9.9 0 1 0 12.04 2zm0 1.8a8.1 8.1 0 1 1-4.3 14.95l-.3-.18-3.1.82.83-3.02-.2-.31A8.1 8.1 0 0 1 12.04 3.8zm-3.1 3.9c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.1 4.5 2.5 1.08 3 .87 3.54.82.54-.05 1.74-.7 1.98-1.4.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35-.3-.15-1.74-.86-2.01-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.47-.89-.8-1.5-1.77-1.67-2.07-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.2-.24-.58-.49-.5-.67-.51h-.57z" />
        </svg>
        {wa === null ? 'No number' : 'WhatsApp'}
      </a>
    </div>
  );
};

// ---------------------------------------------------------------- log a call

export const LogCallDrawer = ({ ctx, student, onClose }: { ctx: DeskContext; student: StudentRow; onClose: () => void }) => {
  const shell = getShellColors(ctx.scheme);
  const [form, setForm] = useState<CallOutcomeForm>({ ...EMPTY_OUTCOME, status: '' });
  const [errors, setErrors] = useState<OutcomeErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmFinal, setConfirmFinal] = useState(false);
  const final = isFinalStatus(form.status);

  const submit = async (): Promise<void> => {
    const found = validateOutcome(form);

    setErrors(found);

    if (Object.keys(found).length > 0) return;

    // A final outcome closes the student, and for a telecaller that is not undoable: confirm first.
    if (final && ctx.profile.lockedWhenFinal && !confirmFinal) return void setConfirmFinal(true);

    setBusy(true);
    setFailure(null);

    try {
      await logCall(student.id, form);
      await ctx.refresh();
      onClose();
    } catch (cause) {
      setConfirmFinal(false);
      setFailure(cause instanceof Error ? cause.message : 'Could not log the call.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Drawer
        scheme={ctx.scheme}
        title={`Log call: ${student.name ?? 'student'}`}
        subtitle={studentPhone(student)}
        onClose={onClose}
        footer={
          <>
            <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void submit()}>
              {busy ? 'Saving…' : 'Save call'}
            </DeskButton>
            <DeskButton scheme={ctx.scheme} onClick={onClose}>
              Cancel
            </DeskButton>
            <ErrorLine scheme={ctx.scheme} message={failure} />
          </>
        }
      >
        <ContactButtons student={student} callerName={ctx.person} />
        <div style={{ height: SHELL_SPACE[4] }} />

        <Field scheme={ctx.scheme} label="How did the call go?" required error={errors.status}>
          <DeskSelect
            scheme={ctx.scheme}
            value={form.status}
            onChange={(status) => setForm({ ...form, status })}
            options={[
              { value: '', label: '-- Choose the outcome --' },
              ...statusChoices().map((c) => ({ value: c.value, label: c.final ? `${c.label} (final)` : c.label })),
            ]}
          />
        </Field>

        {final ? (
          <Notice tone="orange">
            {ctx.profile.lockedWhenFinal ? 'This is a final outcome. The student leaves your call queue and is locked; only your counsellor can reopen it.' : 'This is a final outcome. The student leaves the call queue.'}
          </Notice>
        ) : (
          <Field scheme={ctx.scheme} label="When should the next call be?" required error={errors.nextCallAt}>
            <DeskInput scheme={ctx.scheme} type="datetime-local" value={form.nextCallAt} onChange={(nextCallAt) => setForm({ ...form, nextCallAt })} />
          </Field>
        )}

        <Field scheme={ctx.scheme} label="Follow-up status" error={errors.followUpStatus}>
          <DeskSelect
            scheme={ctx.scheme}
            value={form.followUpStatus}
            onChange={(followUpStatus) => setForm({ ...form, followUpStatus })}
            options={[{ value: '', label: '— None —' }, ...Object.entries(FOLLOW_UP_LABELS).map(([value, label]) => ({ value, label }))]}
          />
        </Field>

        <Field scheme={ctx.scheme} label={`Note (${form.note.length}/${MAX_NOTE})`} error={errors.note}>
          <textarea
            value={form.note}
            rows={4}
            maxLength={MAX_NOTE + 50}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
            style={{ width: '100%', boxSizing: 'border-box', padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`, borderRadius: SHELL_RADIUS.sm, border: `1px solid ${shell.borderMedium}`, background: shell.background, color: shell.text, resize: 'vertical', ...SHELL_TYPE.body }}
          />
        </Field>
      </Drawer>

      {confirmFinal && (
        <ConfirmDialog
          scheme={ctx.scheme}
          title="Close this student?"
          message={<>Marking <strong>{student.name}</strong> as <strong>{DESK_STATUS_LABELS[form.status]}</strong> takes them off your list and locks them. Only your counsellor can reopen them.</>}
          confirmLabel="Yes, close"
          busy={busy}
          error={failure}
          onConfirm={() => void submit()}
          onCancel={() => setConfirmFinal(false)}
        />
      )}
    </>
  );
};

// ------------------------------------------------------------------ the queue

const Card = ({ ctx, student, onLog, onOpen }: { ctx: DeskContext; student: StudentRow; onLog: () => void; onOpen: () => void }) => {
  const shell = getShellColors(ctx.scheme);
  const locked = isLockedFor(ctx.role, student);
  const overdue = !locked && student.nextCallAt !== null && student.nextCallAt !== undefined && new Date(student.nextCallAt).getTime() < Date.now();

  return (
    <div
      style={{
        border: `1px solid ${overdue ? v('color-red') : shell.borderMedium}`,
        borderRadius: SHELL_RADIUS.md,
        padding: SHELL_SPACE[3],
        background: shell.background,
        opacity: locked ? 0.7 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: SHELL_SPACE[2],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: SHELL_SPACE[2], flexWrap: 'wrap' }}>
        <button type="button" onClick={onOpen} style={{ border: 'none', background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', minWidth: 0 }}>
          <div style={{ ...SHELL_TYPE.sectionTitle, color: shell.text }}>{student.name ?? '—'}{student.isPotential === true ? ' 🔥' : ''}</div>
          <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>{studentPhone(student)}</div>
        </button>
        <StatusChip label={DESK_STATUS_LABELS[student.deskStatus ?? ''] ?? '—'} color={DESK_STATUS_COLORS[student.deskStatus ?? ''] ?? 'gray'} />
      </div>

      <div style={{ ...SHELL_TYPE.caption, color: overdue ? v('color-red') : shell.textTertiary }}>
        {locked ? 'Final: locked' : dueLabel(student.nextCallAt)}
        {student.city ? ` · ${student.city}` : ''}
        {student.parentName ? ` · Parent: ${student.parentName}` : ''}
      </div>

      {(student.notes ?? '') !== '' && <div style={{ ...SHELL_TYPE.caption, color: shell.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{student.notes}</div>}

      {locked ? (
        <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }} title={LOCKED_MESSAGE}>
          Closed. Ask your counsellor to reopen it for another call.
        </div>
      ) : (
        <>
          <ContactButtons student={student} callerName={ctx.person} onCallInitiated={onLog} />
          <DeskButton scheme={ctx.scheme} primary onClick={onLog} disabled={student.isBlocked === true} title={student.isBlocked === true ? 'This student is blocked' : 'Record the outcome of your call'}>
            Log call
          </DeskButton>
        </>
      )}
    </div>
  );
};

export const CallQueueScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const sections = callSections(ctx.students);
  const [logging, setLogging] = useState<StudentRow | null>(null);
  const [opened, setOpened] = useState<StudentRow | null>(null);
  const closed = ctx.students.filter((s) => isFinalStatus(s.deskStatus));
  const open = sections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <>
      <div style={{ marginBottom: SHELL_SPACE[4] }}>
        <div style={{ ...SHELL_TYPE.pageTitle, color: shell.text }}>Call Queue</div>
        <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>
          {open === 0 ? 'Nothing to call right now.' : `${open} student${open === 1 ? '' : 's'} to call. Overdue first, then today.`}
        </div>
      </div>

      <ShellGrid min={130}>
        <ShellStat scheme={ctx.scheme} label="Overdue" value={sections[0].rows.length} />
        <ShellStat scheme={ctx.scheme} label="Today" value={sections[1].rows.length} />
        <ShellStat scheme={ctx.scheme} label="New: first call" value={sections[2].rows.length} />
        <ShellStat scheme={ctx.scheme} label="Closed by you" value={closed.length} />
      </ShellGrid>
      <div style={{ height: SHELL_SPACE[4] }} />

      {open === 0 && closed.length === 0 && (
        <ShellCard scheme={ctx.scheme}>
          <ShellEmptyState scheme={ctx.scheme} message="No students have been handed to you yet." hint="Your counsellor assigns students to you. They will appear here, with Call and WhatsApp buttons." />
        </ShellCard>
      )}

      {sections.map((section) =>
        section.rows.length === 0 ? null : (
          <section key={section.id} style={{ marginBottom: SHELL_SPACE[5] }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[2], marginBottom: SHELL_SPACE[2] }}>
              <span style={{ ...SHELL_TYPE.sectionTitle, color: shell.text }}>{section.title}</span>
              <ShellChip label={String(section.rows.length)} color={section.id === 'OVERDUE' ? 'red' : section.id === 'TODAY' ? 'blue' : 'gray'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: SHELL_SPACE[3] }}>
              {section.rows.map((student) => (
                <Card key={student.id} ctx={ctx} student={student} onLog={() => setLogging(student)} onOpen={() => setOpened(student)} />
              ))}
            </div>
          </section>
        ),
      )}

      {closed.length > 0 && (
        <section style={{ marginBottom: SHELL_SPACE[5] }}>
          <div style={{ ...SHELL_TYPE.sectionTitle, color: shell.textSecondary, marginBottom: SHELL_SPACE[2] }}>Closed by you (locked)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: SHELL_SPACE[3] }}>
            {closed.map((student) => (
              <Card key={student.id} ctx={ctx} student={student} onLog={() => undefined} onOpen={() => setOpened(student)} />
            ))}
          </div>
        </section>
      )}

      {logging !== null && <LogCallDrawer ctx={ctx} student={logging} onClose={() => setLogging(null)} />}
      {opened !== null && <DetailsDrawer ctx={ctx} student={opened} onClose={() => setOpened(null)} />}
    </>
  );
};
